import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, generatePath } from 'react-router-dom';
import { toast } from 'sonner';
import { EngineService } from '@/services/engine/engine-service';
import { AppRoutes } from '@/constants/routes';
import { CACHE_KEYS } from '@/constants/cache-keys';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { revalidateByCacheKeyPrefix } from '@/utils/swr';
import { MAX_PROMPT_TEXT_LENGTH, ReportEventType } from '@/types/report';
import type {
    AddReportEventInput,
    AddReportEventsResult,
    ReportEventDraft,
    ReportListItem,
} from '@/types/report';
import type { Event } from '@/types/event';

interface AddEventsToReportArgs {
    report: ReportListItem;
    drafts: ReportEventDraft[];
}

type SceneEvent = Event & { startTime: number; endTime: number };

const isSceneEvent = (event: Event): event is SceneEvent =>
    event.type === 'SCENE' &&
    event.startTime !== undefined &&
    event.endTime !== undefined;

const reportEventInput = (draft: ReportEventDraft): AddReportEventInput => {
    const promptText = draft.promptText.trim().slice(0, MAX_PROMPT_TEXT_LENGTH);
    const description = draft.description.trim();
    if (isSceneEvent(draft.event)) {
        return {
            type: ReportEventType.CUSTOM,
            sourceId: draft.event.sourceId,
            promptText,
            description,
            confidence: draft.confidence,
            videoStartSeconds: draft.event.startTime,
            videoEndSeconds: draft.event.endTime,
            thumbnailFrames: draft.thumbnails,
        };
    }
    return {
        type: ReportEventType.TRACKED_OBJECT,
        eventId: draft.event.id,
        promptText,
        description,
        confidence: draft.confidence,
        thumbnailFrames: draft.thumbnails,
    };
};

const revalidateReportViews = () =>
    Promise.all([
        revalidateByCacheKeyPrefix(CACHE_KEYS.REPORTS_LIST),
        revalidateByCacheKeyPrefix(CACHE_KEYS.REPORT_EVENTS_PREFIX),
    ]);

// A request that never reached the server is reported as a whole-batch failure,
// so callers only ever read the outcome off the counts.
const submitReportEvents = async (
    report: ReportListItem,
    inputs: AddReportEventInput[],
): Promise<AddReportEventsResult> => {
    try {
        const result = await EngineService.addReportEvents(report.id, inputs);
        await revalidateReportViews();
        return result;
    } catch {
        return { added: 0, failed: inputs.length };
    }
};

const reportLinkComponents = (report: ReportListItem) => ({
    reportLink: (
        <Link
            to={generatePath(AppRoutes.REPORT, {
                reportId: report.id,
            })}
            className="underline"
        />
    ),
});

export function useAddEventsToReport() {
    const { t } = useTranslation(I18nNamespace.EVENTS);
    const [busy, setBusy] = useState(false);

    const addEventsToReport = async ({
        report,
        drafts,
    }: AddEventsToReportArgs): Promise<AddReportEventsResult> => {
        if (busy || drafts.length === 0) {
            return { added: 0, failed: 0 };
        }
        setBusy(true);
        try {
            const result = await submitReportEvents(
                report,
                drafts.map(reportEventInput),
            );
            if (result.added === 0) {
                toast.error(
                    t('addToReport.bulkError', { count: result.failed }),
                );
                return result;
            }
            if (result.failed > 0) {
                toast.warning(
                    <Trans
                        t={t}
                        i18nKey="addToReport.bulkPartial"
                        values={{
                            added: result.added,
                            total: result.added + result.failed,
                            failed: result.failed,
                            name: report.name,
                        }}
                        components={reportLinkComponents(report)}
                    />,
                );
                return result;
            }
            toast.success(
                <Trans
                    t={t}
                    i18nKey="addToReport.bulkSuccess"
                    count={result.added}
                    values={{ name: report.name }}
                    components={reportLinkComponents(report)}
                />,
            );
            return result;
        } finally {
            setBusy(false);
        }
    };

    return { busy, addEventsToReport };
}
